import React, { FC, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Flex,
  IconButton,
  SecondaryButton,
  Text,
} from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { theme } from '@moonpig/launchpad-theme'
import { IconSystemCross } from '@moonpig/launchpad-assets'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { useFindLocaleText } from '../../text-localisation'
import { ApplicableFilter, SearchFilter } from '../../services/types/services'
import { getFacetKeyFromOccasion } from './ocassionMap'
import { useFormatReminderTitle } from './useFormatReminderTitle'
import { ReminderPromptAnswer } from './sessionStorage'
import { PromptReminder } from './types'
import { PROMPT_CLOSING_TIME_MS } from './utils'

const MAX_WIDTH_REMINDER_PROMPT = '600px'

type ReminderPromptProps = {
  preAppliedFilters: SearchFilter[]
  selectedFilters: ApplicableFilter[]
  onAnswer: (value: ReminderPromptAnswer) => void
  reminder: PromptReminder
}

const StyledFlex = styled(Flex)`
  animation: slideIn 400ms ease-out forwards;

  &.closing {
    animation: fadeOut ${PROMPT_CLOSING_TIME_MS}ms ease-out forwards;
  }

  @keyframes slideIn {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
`

export const ReminderPrompt: FC<ReminderPromptProps> = ({
  preAppliedFilters,
  selectedFilters,
  onAnswer,
  reminder,
}) => {
  const localiseText = useFindLocaleText()
  const [isClosing, setIsClosing] = useState(false)

  const reminderPromptTitle = useFormatReminderTitle(reminder)

  const reminderPromptTitleForGATracking = reminderPromptTitle
    .replace(reminder.name, 'X')
    .toLowerCase()

  const filterKeys = useMemo(() => {
    const selectedFiltersKeys = selectedFilters.map(filter => filter.id)
    const selectedPreAppliedFiltersKeys = preAppliedFilters.map(
      filter => filter.key,
    )
    return [...selectedFiltersKeys, ...selectedPreAppliedFiltersKeys]
  }, [selectedFilters, preAppliedFilters])

  useEffect(() => {
    if (filterKeys.includes(getFacetKeyFromOccasion(reminder.occasion))) {
      trackGAEvent({
        event: 'impression',
        event_data: {
          category: 'reminders',
          action: 'view prompt',
          label: `reminder prompt | ${reminderPromptTitleForGATracking}`,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminder.occasion, reminderPromptTitleForGATracking])

  if (!filterKeys.includes(getFacetKeyFromOccasion(reminder.occasion))) {
    return null
  }

  return (
    <StyledFlex
      className={isClosing ? 'closing' : ''}
      aria-live="polite"
      role="region"
      aria-label={localiseText('find.reminder_prompt_aria_label')}
      padding={6}
      maxWidth={{ md: MAX_WIDTH_REMINDER_PROMPT }}
      margin={{ xs: 6, md: 8, lg: 8 }}
      justifySelf="center"
      justifyContent="space-between"
      borderRadius={10}
      bgcolor={theme.colors.colorBackground03}
      boxShadow={theme.shadows.boxShadow01}
    >
      <Box
        display="flex"
        flexDirection="row"
        width="100%"
        gap={{ xs: 16, md: 60, lg: 60 }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
      >
        <Text
          id="reminder-prompt-title"
          width={{ xs: '60%', md: '100%' }}
          color={theme.colors.colorTextHeadline}
          typography={'typeDisplay05'}
        >
          {reminderPromptTitle}
        </Text>
        <Flex alignItems="center" gap={{ xs: 8, lg: 16 }}>
          <SecondaryButton
            paddingX={{ xs: 8, md: 12 }}
            maxWidth={{ xs: 64, md: 80 }}
            onClick={() => {
              setIsClosing(true)
              onAnswer(ReminderPromptAnswer.Yes)
              trackGAEvent({
                event: 'select_content',
                event_data: {
                  category: 'reminders',
                  action: 'yes selected',
                  label: `reminder prompt | ${reminderPromptTitleForGATracking}`,
                },
              })
            }}
          >
            {localiseText('find.reminder_prompt_yes')}
          </SecondaryButton>
          <IconButton
            icon={IconSystemCross}
            label={localiseText('common.close')}
            onClick={() => {
              setIsClosing(true)
              onAnswer(ReminderPromptAnswer.No)
              trackGAEvent({
                event: 'select_content',
                event_data: {
                  category: 'reminders',
                  action: 'close selected',
                  label: `reminder prompt | ${reminderPromptTitleForGATracking}`,
                },
              })
            }}
          />
        </Flex>
      </Box>
    </StyledFlex>
  )
}
